import React, { Component } from 'react';
import { createOutlines } from '../../../helpers/svg';
import { getEventCoordinates } from '../../../helpers/touchEvents';
import Styled from './styles';
import { getAdjustedCoordsToSchemeMove } from '../../../helpers/coordinates';
import { ZOOM_MODULE_IDS } from '../../../lib/widget/zoom/zoom.types';
import type { IZoomController } from '../../../lib/widget/zoom/zoom.interface';
import { BACKGROUND_MODULE_IDS } from '../../../lib/widget/background/background.types';
import type { IBackgroundController } from '../../../lib/widget/background/background.interface';
import { STATE_MODULE_IDS } from '../../../lib/widget/state/state.types';
import type { IStateController } from '../../../lib/widget/state/state.interface';
import { resolve } from 'inversify-react';
import { ZoomController } from '../../../lib/widget/zoom/zoom.controller';
import { sendGoal } from '../../../helpers/metrika';
import GOALS from '../../../constants/GOALS';

class Minimap extends Component<any, any> {
  node: any;

  coeff: number;

  minimapWidth: number;

  minimapHeight: number;

  positionNode: any;

  widthCoeff: number;

  heightCoeff: number;

  startX: number;

  startY: number;

  diffX: number;

  diffY: number;

  bbox: {
    top: number;
    left: number;
  };

  isDragging: boolean;

  init: boolean = false;

  zoomController: IZoomController;
  backgroundController: IBackgroundController;
  stateController: IStateController;

  @resolve(ZoomController) ZoomController;

  constructor(props, container) {
    super(props);
    props.getOnDragCallback(this.onDragScheme);

    this.state = {
      zoomIsMinimum: true,
    };

    this.zoomController = container.get(ZOOM_MODULE_IDS.ZOOM_CONTROLLER);
    this.backgroundController = container.get(BACKGROUND_MODULE_IDS.BACKGROUND_CONTROLLER);
    this.stateController = container.get(STATE_MODULE_IDS.STATE_CONTROLLER);

    this.zoomController.addListener(this.zoomController.events.UPDATE, () => {
      const { zoomIsMinimum } = this.zoomController;

      this.setState({
        zoomIsMinimum,
      });
    });
    this.backgroundController.addListener(this.backgroundController.events.INIT, this.renderSvg);
    this.backgroundController.addListener(this.backgroundController.events.UPDATE, this.update);
  }

  renderSvg = () => {
    if (!window.Widget.schemeParameters.hallLayoutMeta) return;
    if (this.init) return;
    const { outlines } = this.backgroundController;
    const { width, height } = window.Widget.schemeParameters.hallLayoutMeta;

    const { svg: svgNode } = createOutlines('minimap', outlines, {
      width,
      height,
      clickable: false,
    });
    this.node = svgNode;
    this.coeff = width / height;
    let newHeight = 120;
    let newWidth = newHeight * this.coeff;

    if (newWidth > 270) {
      this.coeff = height / width;
      newWidth = 270;
      newHeight = newWidth * this.coeff;
    }

    if (newWidth && newHeight) {
      this.node.setAttribute('width', newWidth);
      this.node.setAttribute('height', newHeight);
      this.minimapWidth = newWidth;
      this.minimapHeight = newHeight;

      this.node.addEventListener('click', this.moveScheme);
    }

    this.init = true;
  };

  update = () => {
    if (this.node && this.positionNode) {
      const { schemeHeight, schemeWidth } = window.Widget.schemeParameters;
      const { x, y, x2, y2 } = window.Widget.getViewport();
      const width = this.node.width.baseVal.value;
      const height = this.node.height.baseVal.value;
      this.widthCoeff = schemeWidth / width;
      this.heightCoeff = schemeHeight / height;
      const top = (y / this.heightCoeff) * window.Widget.dpr;
      const left = (x / this.widthCoeff) * window.Widget.dpr;
      const positionWidth = ((x2 - x) / this.widthCoeff) * window.Widget.dpr;
      const positionHeight = ((y2 - y) / this.heightCoeff) * window.Widget.dpr;
      this.positionNode.style.transform = 'none';
      this.positionNode.style.width = `${positionWidth}px`;
      this.positionNode.style.height = `${positionHeight}px`;
      this.positionNode.style.top = `${top}px`;
      this.positionNode.style.left = `${left}px`;
      this.positionNode.top = top;
      this.positionNode.left = left;
      this.positionNode.width = positionWidth;
      this.positionNode.height = positionHeight;
    }
  };

  startMove = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (this.zoomController.isZooming || this.stateController.isZooming) return;
    const { pageX, pageY } = getEventCoordinates(event);

    this.startX = pageX;
    this.startY = pageY;

    this.diffX = 0;
    this.diffY = 0;
    this.isDragging = true;
    this.stateController.setState({ isMinimapMoving: true });

    window.addEventListener('mousemove', this.handleMouseMove);
    window.addEventListener('mouseup', this.handleMouseUp);
    this.positionNode.addEventListener('touchmove', this.handleTouchMove);
    this.positionNode.addEventListener('touchend', this.handleTouchEnd);
    sendGoal(GOALS.USE_MINIMAP);
  };

  handleMouseMove = (event) => {
    event.preventDefault();
    const { pageX, pageY } = event;

    this.movePosition(pageX, pageY);
  };

  handleTouchMove = (event) => {
    event.preventDefault();
    const { pageX, pageY } = getEventCoordinates(event);

    this.movePosition(pageX, pageY);
  };

  movePosition(pageX, pageY) {
    const { scaleX, scaleY } = window.Widget.getScale();
    const diffX = pageX - this.startX;
    const diffY = pageY - this.startY;
    const { left, top, width, height } = this.positionNode;

    const newLeft = left + diffX;
    const newTop = top + diffY;

    if (newLeft > 0 && newLeft + width < this.minimapWidth) {
      this.diffX = diffX;
    }

    if (newTop > 0 && newTop + height < this.minimapHeight) {
      this.diffY = diffY;
    }

    this.positionNode.style.transform = `translate(${this.diffX}px, ${this.diffY}px)`;

    const minimapX = -this.diffX * this.widthCoeff * scaleX;
    const minimapY = -this.diffY * this.heightCoeff * scaleY;

    this.props.onDrag(minimapX, minimapY);
  }

  handleMouseUp = (event) => {
    event.preventDefault();

    this.endMove();
  };

  handleTouchEnd = (event) => {
    event.preventDefault();

    this.endMove();
  };

  endMove() {
    window.removeEventListener('mousemove', this.handleMouseMove);
    window.removeEventListener('mouseup', this.handleMouseUp);
    this.positionNode.removeEventListener('touchmove', this.handleTouchMove);
    this.positionNode.removeEventListener('touchend', this.handleTouchEnd);

    const { scaleX, scaleY } = window.Widget.getScale();
    const { dpr } = window.Widget;

    const { left, top } = this.positionNode;
    const newTop = top + this.diffY;
    const newLeft = left + this.diffX;

    this.positionNode.style.transform = 'none';
    this.positionNode.style.top = `${newTop}px`;
    this.positionNode.style.left = `${newLeft}px`;
    this.positionNode.top = newTop;
    this.positionNode.left = newLeft;

    window.Widget.draggingController.pan({
      offsetX: ((-this.diffX / dpr) * this.widthCoeff * scaleX) / dpr,
      offsetY: ((-this.diffY / dpr) * this.heightCoeff * scaleY) / dpr,
    });

    this.startX = 0;
    this.startY = 0;
    this.diffX = 0;
    this.diffY = 0;
    this.isDragging = false;
    this.stateController.setState({ isMinimapMoving: false });
  }

  getPositionNode = (node) => {
    if (node) {
      this.positionNode = node;

      this.positionNode.addEventListener('mousedown', this.startMove);
      this.positionNode.addEventListener('touchstart', this.startMove);
    }
  };

  onDragScheme = (diffX, diffY) => {
    if (!this.positionNode) return;

    const { dpr } = window.Widget;
    const { scaleX, scaleY } = window.Widget.getScale();

    const ty = (diffY * dpr) / this.heightCoeff / scaleY;
    const tx = (diffX * dpr) / this.widthCoeff / scaleX;

    this.positionNode.style.transform = `translate(${-tx * dpr}px, ${-ty * dpr}px)`;
  };

  moveScheme = (event) => {
    const { x, y } = getAdjustedCoordsToSchemeMove({
      node: this.node,
      xCoord: event.pageX,
      yCoord: event.pageY,
    });

    window.Widget.draggingController.moveTo({ x, y });
  };

  handleMouseMoveOnContainer = (event) => {
    const { isDragging } = this.stateController;

    if (!isDragging && !this.isDragging) {
      event.stopPropagation();
    }
  };

  render() {
    const { zoomIsMinimum } = this.state;
    const { isFullscreen } = this.props;
    const { isPeriodical } = window.Widget.metaController.meta;

    return (
      <Styled.Container
        isFullscreen={isFullscreen}
        isHidden={zoomIsMinimum}
        isPeriodical={isPeriodical}
        onMouseMove={this.handleMouseMoveOnContainer}
      >
        <Styled.Minimap id="minimap" />
        <Styled.CurrentPosition ref={this.getPositionNode} />
      </Styled.Container>
    );
  }
}

export default Minimap;
